export interface TenantStats {
    id: string;
    name: string;
    website: string | null;
    billingEmail: string | null;
    createdAt: string;
    agentCount: number;
    visitorCount: number;
}

export const getTenants = async (): Promise<TenantStats[]> => {
    const res = await fetch('/api/backoffice/tenants');
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to fetch tenants: ${res.statusText}`);
    }
    return res.json();
};

export const impersonateTenant = async (accountId: string): Promise<void> => {
    const res = await fetch('/api/backoffice/impersonate', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accountId })
    });

    if (!res.ok) {
        throw new Error('Failed to impersonate tenant');
    }
};

export const getTenantStats = async (id: string) => {
    const res = await fetch(`/api/backoffice/tenants/${id}/stats`);
    return res.json();
};

export const getTenantTraffic = async (id: string) => {
    const res = await fetch(`/api/backoffice/tenants/${id}/traffic`);
    return res.json();
};

export const getTenantEvents = async (id: string) => {
    const res = await fetch(`/api/backoffice/tenants/${id}/events`);
    return res.json();
};

// WhatsApp connect (P2c-3) — POST the per-tenant WABA credential to the store endpoint. The access
// token is sent in the JSON body (same-origin, TLS), NEVER in the URL, and the response carries only
// non-secret identifiers. A cross-tenant number reclaim returns 409 → surfaced as a distinct error.
export interface WhatsAppConnectInput {
    accountId: string;
    phoneNumberId: string;
    wabaId: string;
    // Optional (AC-W6): blank on a save keeps the stored token; a value replaces it. Required only on a
    // first-time connection (the server enforces that — nothing to keep).
    accessToken?: string;
    displayNumber?: string;
}
export interface WhatsAppConnectResult { ok: boolean; accountId: string; phoneNumberId: string; }

/** Non-secret credential view for the per-account section (AC-W4). Never carries the token. */
export interface SafeWhatsAppCredential {
    phoneNumberId: string;
    wabaId: string;
    displayNumber: string | null;
    status: string;
    tokenConfigured: boolean;
}

/** 409 — the WABA number is already connected to a DIFFERENT tenant (global phoneNumberId uniqueness). */
export class WhatsAppNumberConflictError extends Error {}

// WhatsApp APP config (P2c App-Secret delivery) — POST the app secret + verify token to the encrypted
// global-config store. Both are sent in the JSON body (TLS), never the URL; the response has no secret.
export const saveWhatsAppAppConfig = async (input: { appSecret: string; verifyToken: string }): Promise<{ ok: boolean }> => {
    const res = await fetch('/api/backoffice/whatsapp/app-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to save WhatsApp app config: ${res.statusText}`);
    }
    return res.json();
};

export const connectWhatsAppCredential = async (input: WhatsAppConnectInput): Promise<WhatsAppConnectResult> => {
    const res = await fetch('/api/backoffice/whatsapp/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
    });
    if (res.status === 409) {
        const err = await res.json().catch(() => ({}));
        throw new WhatsAppNumberConflictError(err.error || 'This WhatsApp number is already connected to another tenant.');
    }
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to connect WhatsApp: ${res.statusText}`);
    }
    return res.json();
};

/** Read an account's WhatsApp credential for display (AC-W4). Returns null when not connected (AC-W3).
 *  The response carries non-secret fields + a tokenConfigured indicator only — never the token. */
export const getWhatsAppCredential = async (accountId: string): Promise<SafeWhatsAppCredential | null> => {
    const res = await fetch(`/api/backoffice/whatsapp/credentials/${encodeURIComponent(accountId)}`);
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to read WhatsApp credential: ${res.statusText}`);
    }
    const data = await res.json();
    return data.credential ?? null;
};
